import { Logo } from "@/components/Logo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SmartLink } from "@/components/landing/HashLink";
import { EmailLink } from "@/components/EmailLink";

export const FOOTER_COLUMNS = [
  { title: "Explore Learning", links: [["Courses & Learning Paths", "#courses"], ["Pricing", "#pricing"]] },
  { title: "For Organisations", links: [["Organisation Plans", "#organisations"], ["Talent & Hiring", "#employers"]] },
  { title: "Company", links: [["About Us", "/about"], ["How It Works", "#how-it-works"], ["Contact Us", "/contact"]] },
  { title: "Help & Support", links: [["Help Centre", "/help"], ["Frequently Asked Questions", "#faq"], ["Account & Billing", "/account-billing"]] },
];

export const LEGAL_LINKS = [
  ["Privacy Policy", "/privacy"], ["Terms of Service", "/terms"], ["Cookie Policy", "/cookies"],
  ["Learning & Assessment Disclaimer", "/learning-disclaimer"], ["Refund & Cancellation Policy", "/refunds"],
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");
const linkCls = "rounded-sm text-sm text-slate-700 underline-offset-4 hover:text-[#222d99] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#222d99]";

const LinkList = ({ links, prefix }) => (
  <ul className="space-y-2.5">
    {links.map(([label, to]) => (
      <li key={label}>
        <SmartLink to={to} className={linkCls} data-testid={`${prefix}-${slug(label)}`}>{label}</SmartLink>
        {label === "Contact Us" && <div><EmailLink icon className="text-sm" testid={`${prefix}-email`} /></div>}
      </li>
    ))}
  </ul>
);

export function SiteFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white" data-testid="site-footer">
      <div className="mx-auto max-w-6xl px-4 pt-12 sm:px-6">
        <Logo />
        <p className="mt-3 max-w-md text-sm text-slate-700">Career readiness for job seekers, graduates and career changers.</p>
        <EmailLink className="text-sm" testid="footer-email-brand" />
        <p className="mt-1 text-sm font-medium text-slate-900">Profiles are private by default.</p>

        <nav aria-label="Footer" className="mt-10">
          <div className="hidden gap-8 sm:grid sm:grid-cols-2 lg:grid-cols-4" data-testid="footer-columns">
            {FOOTER_COLUMNS.map((c) => (
              <div key={c.title}>
                <h2 className="mb-4 text-sm font-semibold text-slate-900">{c.title}</h2>
                <LinkList links={c.links} prefix="footer-link" />
              </div>
            ))}
          </div>
          <Accordion type="multiple" className="sm:hidden" data-testid="footer-accordion">
            {FOOTER_COLUMNS.map((c) => (
              <AccordionItem key={c.title} value={c.title}>
                <AccordionTrigger className="text-sm font-semibold text-slate-900" data-testid={`footer-accordion-${slug(c.title)}`}>{c.title}</AccordionTrigger>
                <AccordionContent><LinkList links={c.links} prefix="footer-mlink" /></AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </nav>

        <div className="mt-10 border-t border-slate-200 py-6">
          <h2 className="sr-only">Legal & Policies</h2>
          <ul className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-0" aria-label="Legal & Policies" data-testid="footer-legal">
            {LEGAL_LINKS.map(([label, to], i) => (
              <li key={to} className="flex items-center">
                {i > 0 && <span className="mx-3 hidden text-slate-400 sm:inline" aria-hidden="true">|</span>}
                <SmartLink to={to} className={linkCls} data-testid={`footer-legal-${to.slice(1)}`}>{label}</SmartLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200 px-4 py-5 text-center text-xs text-slate-700">© {new Date().getFullYear()} Crowlearn. All rights reserved.</div>
    </footer>
  );
}
